<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { get } from 'lodash-es';
import Download from '../PaymentsDownload';
import Overlay from '../../TwOverlay';

export default {
  name: 'AccumulationTable',
  components: {
    Download,
    Overlay,
  },
  computed: {
    ...mapState({
      radio: state => state.supplierPayments.filter.data.selectedTableType,
      brands: state => state.supplierPayments.filter.data.brands,
      stays: state => state.supplierPayments.tableData.stays,
      loading: state => state.supplierPayments.tableData.loading,
      loaded: state => state.supplierPayments.tableData.loaded,
      error: state => state.supplierPayments.tableData.error,
      downloadListLoading: state => state.supplierPayments.download.list.loading,
      downloadListError: state => state.supplierPayments.download.list.error,
      downloadSpecLoading: state => state.supplierPayments.download.specifications.loading,
      downloadSpecError: state => state.supplierPayments.download.specifications.error,
      downloadSpecName: state => state.supplierPayments.download.specifications.name,
    }),
    ...mapGetters({
      headers: 'supplierPayments/getSupplierPaymentsAccumulationTableHeaders',
      rows: 'supplierPayments/getSupplierPaymentsAccumulationTableRows',
      downloadInfo: 'supplierPayments/getDownloadSearchInfo',
    }),
    downloadDisabled() {
      return this.loading ||
        this.error ||
        !this.rows ||
        !this.rows.length ||
        this.downloadListLoading ||
        this.downloadSpecLoading;
    },
  },
  methods: {
    ...mapActions({
      downloadList: 'supplierPayments/downloadList',
      downloadSpecifications: 'supplierPayments/downloadSpecifications',
    }),
    tableSort(items, index, descending) {
      return items.sort((a, b) => {
        if ((a.type && !b.type) || (b.type && !a.type)) {
          return a.type ? -1 : 1;
        }
        if (a.type && b.type) {
          return a.type === 1 ? -1 : 1;
        }
        if (!index) {
          return 0;
        }
        const sort = get(a, index) > get(b, index) ? 1 : -1;
        return sort * (descending ? 1 : -1);
      });
    },
    downloadPayments(supplier) {
      if (this.downloadDisabled) {
        return;
      }
      this.downloadSpecifications(supplier);
    },
  },
  filters: {
    formatNumber: number => number.toFixed(2)
      .replace(/\./, ',')
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
  },
  data() {
    return {
      rowsPerPageItems: [5, 10, 25, 50, 100, { text: this.$t('inputs.all'), value: -1 }],
      pagination: {
        rowsPerPage: 25,
      },
    };
  },
};
</script>

<template>
  <v-flex class="accumulation-table xs12 mt-4">
    <h1 v-if="radio === 1" class="my-1 text-xs-center">
      {{ $t('supplierPayments.lists.titles.paid') }}
    </h1>
    <h1 v-else class="my-1 text-xs-center">
      {{ $t('supplierPayments.lists.titles.due') }}
    </h1>
    <download
      @download="downloadList"
      :disabled="downloadDisabled"
      :loading="downloadListLoading"
      :error="downloadListError"
      :error-message="$t('payments.actions.downloadError')"
      :info="downloadInfo"
    />
    <v-divider />
    <v-flex class="accumulation-table__table xs12">
      <overlay :visible="downloadSpecLoading"/>
      <v-data-table
        :headers="headers"
        :items="rows"
        :pagination.sync="pagination"
        :rows-per-page-items="rowsPerPageItems"
        :loading="loading"
        :custom-sort="tableSort"
        disable-initial-sort
      >
        <template slot="items" slot-scope="props">
          <tr v-if="props.item.type === 1" class="accumulation-table__table-row">
            <td/>
            <td>{{ $t('supplierPayments.stays') }}</td>
            <td class="text-xs-right">{{ stays.all.live | formatNumber }}</td>
            <td class="text-xs-right">{{ stays.all.cancelled | formatNumber }}</td>
            <td
              class="text-xs-right"
              v-for="brand in brands"
              :key="'Brand_' + brand.id"
            >
              <v-tooltip top>
                <template slot="activator" slot-scope="{ on }">
                  <div v-on="on">
                    <span>
                      {{ stays[brand.id].total | formatNumber }}
                    </span>
                  </div>
                </template>
                <span>
                  <span>
                    {{ $t('supplierPayments.lists.types.accumulation.live') }}:
                    {{ stays[brand.id].live | formatNumber }}
                  </span>
                  <span class="mx-2">|</span>
                  <span>
                    {{ $t('supplierPayments.lists.types.accumulation.cancelled') }}:
                    {{ stays[brand.id].cancelled | formatNumber }}
                  </span>
                </span>
              </v-tooltip>
            </td>
          </tr>
          <tr v-else-if="props.item.type === 2" class="accumulation-table__table-row">
            <td/>
            <td/>
            <td/>
            <td/>
            <td v-for="brand in brands" :key="'Brand_' + brand.id"></td>
          </tr>
          <tr
            v-else
            @click="downloadPayments(props.item.supplier)"
            class="accumulation-table__table-row accumulation-table__download"
          >
            <td>
              {{ props.item.supplier.name }}
            </td>
            <td>
              {{ props.item.product.name }}
            </td>
            <td class="text-xs-right">
              {{ props.item.payments.total.live | formatNumber }}
            </td>
            <td class="text-xs-right">
              {{ props.item.payments.total.cancelled | formatNumber }}
            </td>
            <td
              v-for="brand in brands"
              class="text-xs-right"
              :key="'Brand_' + brand.id"
            >
              <v-tooltip top>
                <template slot="activator" slot-scope="{ on }">
                  <div v-on="on">
                    <span>
                      {{ props.item.payments.brands[brand.id].total | formatNumber }}
                    </span>
                  </div>
                </template>
                <span>
                  <span>
                    {{ $t('supplierPayments.lists.types.accumulation.live') }}:
                    {{ props.item.payments.brands[brand.id].live | formatNumber }}
                  </span>
                  <span class="mx-2">|</span>
                  <span>
                    {{ $t('supplierPayments.lists.types.accumulation.cancelled') }}:
                    {{ props.item.payments.brands[brand.id].cancelled | formatNumber }}
                  </span>
                </span>
              </v-tooltip>
            </td>
          </tr>
        </template>
        <template slot="no-data">
          <div v-if="error">
            <v-alert :value="true" icon="warning">
              {{ $t('payments.actions.error') }}.
              <span class="accumulation-table__text-link" @click="$emit('refresh')">
                  {{ $t('inputs.misc.tryAgain') }}
                </span>
            </v-alert>
          </div>
          <div v-else-if="loading">
            <!--Show nothing when loading-->
          </div>
          <div v-else-if="!loaded" class="text-xs-center">
            <span>{{ $t('payments.actions.performSearch') }}</span>
          </div>
          <div v-else class="text-xs-center">
            <span>{{ $t('inputs.misc.noContent') }}</span>
          </div>
        </template>
        <template slot="footer">
          <tr v-if="rows.length">
            <td :colspan="headers.length" class="grey--text caption">
              {{ $t('payments.actions.downloadSupplier') }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-flex>
    <v-snackbar
      :value="downloadSpecName"
      :timeout="0"
      bottom
    >
      <v-progress-circular
        color="primary"
        size="25"
        width="3"
        indeterminate
      />
      <span class="ml-3">
        {{ $t('payments.actions.downloadSpecLoading', { name: downloadSpecName }) }}
      </span>
    </v-snackbar>
    <v-snackbar
      :value="downloadSpecError"
      bottom
    >
      {{ $t('payments.actions.downloadSpecError') }}
    </v-snackbar>
  </v-flex>
</template>

<style lang="scss">
  .accumulation-table {
    position: relative;

    &__table {
      position: relative;
    }

    &__download {
      cursor: pointer;
    }

    &__text-link {
      text-decoration: underline;
      cursor: pointer;
    }

    &__table-row > td:not(:first-child) {
      border-left: 1px solid black;
    }

    &__table-row:nth-child(even) {
      background-color: #f5f5f5
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/payments/supplier-payments/SupplierPaymentsAccumulationTable.vue